import React, { useState, useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import { API_BASE_URL } from "../../config/api";



const AdminRepairManager = () => {
  const [repairs, setRepairs] = useState([]);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    surname: "",
    phone: "",
    phoneType: "",
    issueDescription: "",
    clientNote: "",
    technicianConclusion: "",
    readyDate: "",
    pickupDate: "",
  });

  const [dialogOpen, setDialogOpen] = useState(false);

  const navigate = useNavigate();
  const printRef = useRef(null);


  // ================= FETCH REPAIRS =================
  useEffect(() => {
    const fetchRepairs = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const res = await fetch(`${API_BASE_URL}/api/repairs`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) throw new Error("Failed to load repairs");

        const data = await res.json();
        setRepairs(data);
      } catch (err) {
        console.error("Failed to load repairs:", err);
      }
    };

    fetchRepairs();
  }, []);

  // ================= HANDLE CHANGE =================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ================= CREATE / UPDATE =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("authToken");

      if (formData.id) {
        // UPDATE
        const res = await fetch(
          `${API_BASE_URL}/api/repairs/${formData.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formData),
          }
        );

        if (!res.ok) throw new Error("Failed to update repair");

        const updated = await res.json();

        setRepairs((prev) =>
          prev.map((r) => (r.id === updated.id ? updated : r))
        );
      } else {
        // CREATE
        const res = await fetch(`${API_BASE_URL}/api/repairs`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        });

        if (!res.ok) throw new Error("Failed to create repair");

        const newRepair = await res.json();

        setRepairs((prev) => [...prev, newRepair]);
      }

      setFormData({
        id: null,
        name: "",
        surname: "",
        phone: "",
        phoneType: "",
        issueDescription: "",
        clientNote: "",
        technicianConclusion: "",
        readyDate: "",
        pickupDate: "",
      });

      setDialogOpen(false);
    } catch (err) {
      console.error(err);
      alert(err.message || "Error saving repair");
    }
  };

  // ================= EDIT =================
  const handleEdit = (repair) => {
    setFormData({
      id: repair.id,
      name: repair.name || "",
      surname: repair.surname || "",
      phone: repair.phone || "",
      phoneType: repair.phone_type || "",
      issueDescription: repair.issue_description || "",
      clientNote: repair.client_note || "",
      technicianConclusion: repair.technician_conclusion || "",
      readyDate: repair.ready_date?.slice(0, 10) || "",
      pickupDate: repair.pickup_date?.slice(0, 10) || "",
    });

    setDialogOpen(true);
  };

  // ================= DELETE =================
  const handleRemove = async (id) => {
    if (!window.confirm("Are you sure you want to delete this repair?"))
      return;

    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/api/repairs/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Failed to delete repair");

      setRepairs((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error(err);
      alert(err.message || "Error deleting repair");
    }
  };

  // ================= PRINT =================
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  // ================= RENDER =================
  return (
    <div className="bg-gray-100 text-gray-800">
      <main className="flex-1 p-12 ml-64 min-h-screen">
        {/* HEADER */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-extrabold">
            Menaxho serviset
          </h1>

          <button
            onClick={() => {
              setFormData({
                id: null,
                name: "",
                surname: "",
                phone: "",
                phoneType: "",
                issueDescription: "",
                clientNote: "",
                technicianConclusion: "",
                readyDate: "",
                pickupDate: "",
              });
              setDialogOpen(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            + Servis i RI
          </button>
        </div>

        {/* ================= TABLE ================= */}
        {repairs.length > 0 && (
          <div className="overflow-x-auto bg-white rounded-lg shadow-md">
            <table className="min-w-full text-sm border">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="p-3">Emër</th>
                  <th className="p-3">Mbiemër</th>
                  <th className="p-3">Telefon</th>
                  <th className="p-3">Pajisja</th>
                  <th className="p-3">Problemi</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>

              <tbody>
                {repairs.map((repair) => (
                  <tr key={repair.id} className="border-t">
                    <td className="p-2">{repair.name}</td>
                    <td className="p-2">{repair.surname}</td>
                    <td className="p-2">{repair.phone}</td>
                    <td className="p-2">{repair.phone_type}</td>
                    <td className="p-2">{repair.issue_description}</td>

                    <td className="p-2 flex gap-3">
                      <button onClick={() => handleEdit(repair)}>
                        <FaEdit />
                      </button>
                      <button onClick={() => handleRemove(repair.id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ================= FORM MODAL ================= */}
      {dialogOpen && (
        <>
          {/* BACKDROP */}
          <div
            className="fixed inset-0 bg-black/50 z-[9998]"
            onClick={() => setDialogOpen(false)}
          />

          {/* MODAL */}
          <div
            className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] bg-white rounded-xl shadow w-[520px] max-h-[90vh] flex flex-col"
          >
            {/* HEADER */}
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-bold">
                {formData.id ? "Edito Servisin" : "Shto Servis"}
              </h2>

              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="text-xl font-bold text-gray-600 hover:text-red-500"
              >
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className="p-4 space-y-3 overflow-y-auto">

              {/* EMRI */}
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Emri i klientit"
                className="border p-2 w-full rounded"
              />

              {/* MBIEMRI */}
              <input
                name="surname"
                value={formData.surname}
                onChange={handleChange}
                placeholder="Mbiemri i klientit"
                className="border p-2 w-full rounded"
              />

              {/* TELEFONI */}
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Numri i telefonit"
                className="border p-2 w-full rounded"
              />

              {/* TIPI I PAJISJES (INPUT SI KËRKOJTE) */}
              <input
                name="phoneType"
                value={formData.phoneType}
                onChange={handleChange}
                placeholder="Tipi i pajisjes (p.sh iPhone 13, Samsung S22)"
                className="border p-2 w-full rounded"
              />

              {/* PROBLEMI */}
              <textarea
                name="issueDescription"
                value={formData.issueDescription}
                onChange={handleChange}
                placeholder="Përshkrimi i problemit"
                className="border p-2 w-full rounded"
              />

              {/* SHËNIMI I KLIENTIT */}
              <textarea
                name="clientNote"
                value={formData.clientNote}
                onChange={handleChange}
                placeholder="Shënim i klientit"
                className="border p-2 w-full rounded"
              />

              {/* KONKLUZIONI TEKNIK */}
              <textarea
                name="technicianConclusion"
                value={formData.technicianConclusion}
                onChange={handleChange}
                placeholder="Konkluzioni i teknikut"
                className="border p-2 w-full rounded"
              />

              {/* DATA GATI */}
              <label className="text-sm text-gray-600">Data kur bëhet gati</label>
              <input
                type="date"
                name="readyDate"
                value={formData.readyDate}
                onChange={handleChange}
                className="border p-2 w-full rounded"
              />

              {/* DATA MARRJES */}
              <label className="text-sm text-gray-600">Data e marrjes</label>
              <input
                type="date"
                name="pickupDate"
                value={formData.pickupDate}
                onChange={handleChange}
                className="border p-2 w-full rounded"
              />
            </div>

            {/* FOOTER */}
            <div className="p-4 border-t flex gap-2">
              <button
                onClick={handleSubmit}
                className="bg-blue-600 text-white px-4 py-2 rounded w-full"
              >
                Ruaj
              </button>

              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="bg-gray-400 px-4 py-2 rounded w-full"
              >
                Anulo
              </button>
            </div>
          </div>
        </>
      )}
      </main>
    </div>
  );
};

export default AdminRepairManager;